import { useState, useEffect } from "react";
import { dashboardAPI } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  TrendingUp, 
  AlertTriangle, 
  Users, 
  Clock,
  Target,
  Zap,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

function RiskBadge({ level }) {
  const colors = {
    critical: "bg-red-100 text-red-800 border-red-200",
    high: "bg-orange-100 text-orange-800 border-orange-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    low: "bg-green-100 text-green-800 border-green-200"
  };
  return (
    <Badge className={`${colors[level] || colors.low} capitalize`}>
      {level}
    </Badge>
  );
}

function CapacityBadge({ status }) {
  const colors = {
    overloaded: "bg-red-100 text-red-800",
    at_capacity: "bg-orange-100 text-orange-800",
    high: "bg-yellow-100 text-yellow-800",
    available: "bg-green-100 text-green-800"
  };
  const labels = {
    overloaded: "Overloaded",
    at_capacity: "At Capacity",
    high: "High Load",
    available: "Available"
  };
  return (
    <Badge className={colors[status] || colors.available}>
      {labels[status] || status}
    </Badge>
  );
}

export default function PredictiveAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showVelocityDialog, setShowVelocityDialog] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showBehindDialog, setShowBehindDialog] = useState(false);
  const [showOverloadedDialog, setShowOverloadedDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getPredictive();
      setData(response.data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <p className="text-red-600">Error loading analytics: {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { velocity, project_estimates, project_risks, capacity_forecast, summary } = data;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setShowVelocityDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Team Velocity</p>
                <p className="text-2xl font-bold">{velocity.per_week}</p>
                <p className="text-xs text-muted-foreground">tasks/week</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => summary.high_risk_projects > 0 && setShowRiskDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">High Risk Projects</p>
                <p className="text-2xl font-bold text-orange-600">{summary.high_risk_projects}</p>
                <p className="text-xs text-muted-foreground">need attention</p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => summary.projects_behind_schedule > 0 && setShowBehindDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Behind Schedule</p>
                <p className="text-2xl font-bold text-red-600">{summary.projects_behind_schedule}</p>
                <p className="text-xs text-muted-foreground">of {summary.total_active_projects} projects</p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <Clock className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => summary.overloaded_resources > 0 && setShowOverloadedDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overloaded Resources</p>
                <p className="text-2xl font-bold text-purple-600">{summary.overloaded_resources}</p>
                <p className="text-xs text-muted-foreground">above capacity</p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Risk Scores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Project Risk Scores
            </CardTitle>
            <CardDescription>Click any project for details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {project_risks.slice(0, 6).map((project) => (
                <div 
                  key={project.id} 
                  className="flex items-center justify-between p-3 rounded-lg border bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={project.risk_score} className="h-2 w-24" />
                      <span className="text-xs text-muted-foreground">{project.risk_score.toFixed(0)}%</span>
                    </div>
                    {project.risk_factors.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {project.risk_factors[0]}
                      </p>
                    )}
                  </div>
                  <RiskBadge level={project.risk_level} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Completion Estimates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Completion Estimates
            </CardTitle>
            <CardDescription>Predicted vs planned end dates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {project_estimates.slice(0, 6).map((project) => (
                <div key={project.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.name}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{project.completed_tasks}/{project.total_tasks} tasks</span>
                      <span>•</span>
                      <span>{project.days_to_complete} days remaining</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {project.variance_days !== null && (
                      <div className={`flex items-center gap-1 text-sm ${project.variance_days > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {project.variance_days > 0 ? (
                          <>
                            <ArrowUpRight className="h-4 w-4" />
                            +{project.variance_days}d
                          </>
                        ) : (
                          <>
                            <ArrowDownRight className="h-4 w-4" />
                            {project.variance_days}d
                          </>
                        )}
                      </div>
                    )}
                    <Badge variant={project.on_track ? "default" : "destructive"}>
                      {project.on_track ? "On Track" : "Behind"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capacity Forecast */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Capacity Forecast
          </CardTitle>
          <CardDescription>Click any resource for task details (10 tasks = 100% capacity)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capacity_forecast.slice(0, 9).map((resource) => (
              <div 
                key={resource.id} 
                className="p-4 rounded-lg border bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedResource(resource)}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium truncate">{resource.name}</p>
                  <CapacityBadge status={resource.status} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Utilization</span>
                    <span className="font-medium">{resource.utilization_pct}%</span>
                  </div>
                  <Progress 
                    value={Math.min(100, resource.utilization_pct)} 
                    className={`h-2 ${resource.utilization_pct > 100 ? '[&>div]:bg-red-500' : resource.utilization_pct > 80 ? '[&>div]:bg-orange-500' : ''}`}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{resource.active_tasks} active tasks</span>
                    <span>{resource.tasks_due_7d} due this week</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Velocity Details Dialog */}
      <Dialog open={showVelocityDialog} onOpenChange={setShowVelocityDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Team Velocity Details
            </DialogTitle>
            <DialogDescription>Task completion rate over the last 30 days</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg border text-center">
                <p className="text-3xl font-bold text-blue-600">{velocity.tasks_completed_30d}</p>
                <p className="text-xs text-muted-foreground">Tasks (30d)</p>
              </div>
              <div className="p-4 rounded-lg border text-center">
                <p className="text-3xl font-bold">{velocity.per_week}</p>
                <p className="text-xs text-muted-foreground">Per Week</p>
              </div>
              <div className="p-4 rounded-lg border text-center">
                <p className="text-3xl font-bold">{velocity.per_day}</p>
                <p className="text-xs text-muted-foreground">Per Day</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* High Risk Projects Dialog */}
      <Dialog open={showRiskDialog} onOpenChange={setShowRiskDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              High Risk Projects
            </DialogTitle>
            <DialogDescription>Projects with risk score above 45%</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {project_risks.filter(p => p.risk_level === "critical" || p.risk_level === "high").map((project) => (
              <div key={project.id} className="p-3 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{project.name}</p>
                    <p className="text-xs text-muted-foreground">{project.risk_factors.join(" • ")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">{project.risk_score.toFixed(0)}%</span>
                    <RiskBadge level={project.risk_level} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Behind Schedule Dialog */}
      <Dialog open={showBehindDialog} onOpenChange={setShowBehindDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-red-600" />
              Projects Behind Schedule
            </DialogTitle>
            <DialogDescription>Estimated completion after planned end date</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {project_estimates.filter(p => p.variance_days && p.variance_days > 0).map((project) => (
              <div key={project.id} className="p-3 rounded-lg border border-l-4 border-l-red-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{project.name}</p>
                    <p className="text-xs text-muted-foreground">{project.completed_tasks}/{project.total_tasks} tasks complete</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">+{project.variance_days} days</p>
                    <p className="text-xs text-muted-foreground">behind schedule</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Overloaded Resources Dialog */}
      <Dialog open={showOverloadedDialog} onOpenChange={setShowOverloadedDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Overloaded Resources
            </DialogTitle>
            <DialogDescription>Team members above 100% capacity</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {capacity_forecast.filter(r => r.status === "overloaded").map((resource) => (
              <div key={resource.id} className="p-3 rounded-lg border border-l-4 border-l-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{resource.name}</p>
                    <p className="text-xs text-muted-foreground">{resource.role}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">{resource.utilization_pct}%</p>
                    <p className="text-xs text-muted-foreground">{resource.active_tasks} tasks</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Selected Project Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              {selectedProject?.name}
            </DialogTitle>
            <DialogDescription>Risk analysis details</DialogDescription>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <span>Risk Score</span>
                <div className="flex items-center gap-2">
                  <Progress value={selectedProject.risk_score} className="w-24 h-3" />
                  <span className="font-bold">{selectedProject.risk_score.toFixed(0)}%</span>
                  <RiskBadge level={selectedProject.risk_level} />
                </div>
              </div>
              <div className="p-4 rounded-lg border">
                <p className="text-sm font-medium mb-2">Completion</p>
                <p className="text-2xl font-bold">{selectedProject.completion_pct?.toFixed(0) || 0}%</p>
              </div>
              {selectedProject.risk_factors.length > 0 && (
                <div className="p-4 rounded-lg border bg-amber-50">
                  <p className="text-sm font-medium mb-2">Risk Factors</p>
                  <ul className="text-sm space-y-1">
                    {selectedProject.risk_factors.map((factor, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <AlertTriangle className="h-3 w-3 text-amber-600" />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Selected Resource Dialog */}
      <Dialog open={!!selectedResource} onOpenChange={() => setSelectedResource(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedResource?.name}
            </DialogTitle>
            <DialogDescription>{selectedResource?.role}</DialogDescription>
          </DialogHeader>
          {selectedResource && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border text-center">
                  <p className="text-3xl font-bold">{selectedResource.active_tasks}</p>
                  <p className="text-xs text-muted-foreground">Active Tasks</p>
                </div>
                <div className="p-4 rounded-lg border text-center">
                  <p className="text-3xl font-bold">{selectedResource.tasks_due_7d}</p>
                  <p className="text-xs text-muted-foreground">Due This Week</p>
                </div>
              </div>
              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span>Utilization</span>
                  <span className="font-bold">{selectedResource.utilization_pct}%</span>
                </div>
                <Progress 
                  value={Math.min(100, selectedResource.utilization_pct)} 
                  className={`h-3 ${selectedResource.utilization_pct > 100 ? '[&>div]:bg-red-500' : ''}`}
                />
              </div>
              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between">
                  <span>Capacity Remaining</span>
                  <span className="font-bold">{selectedResource.capacity_remaining} tasks</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
